import axios from "axios";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RegisterCard } from "../components/RegisterCard";
import { TopAdminNav } from "../layouts/TopAdminNav";
import { useState, useEffect } from "react";
import "../assets/css/main.css";
import "../assets/css/student.css";
import BackButton from "../components/backButton";
import { Menu, MenuButton, MenuItem, SubMenu } from "@szhsin/react-menu";
import Swal from "sweetalert2";
import CustomModal from "../components/CustomModal/CustomModal";
import { EditStaffForm } from "../layouts/editForms/staff";
import { defaultUrl } from "../features/actions";
import toast from "react-hot-toast";
import { faCheckCircle } from "@fortawesome/free-regular-svg-icons";
import Pagination from "../components/pagination";
import { IPaginationParams } from "../types/interfaces";
import loader from "../assets/loader.gif";
import {
  uploadImageToCloudinary,
  validateUploadFile,
} from "../utilities/importantFns";
import {
  makeFetchRequest,
  makePostRequest,
  makePutRequest,
} from "../utilities/common";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

export interface StaffDataType {
  id: string;
  user_id: string,
  firstname?: string;
  surname?: string;
  lastname?: string;
  othername?: string;
  gender?: string;
  role?: string;
  date_of_birth?: string;
  resumption_date?: string;
  email?: string;
  phone_number?: string;
  degree?: string;
  classrooms?: { id: string; name: string }[];
  discipline?: string;
  house_address?: string;
  house_address2?: string;
  city?: string;
  state?: string;
  country?: string;
  zip_code?: string;
  profile_picture?: string;
  signature?: string;
}

interface ImageObject {
  [key: string]: any;
}

export const StaffData = () => {
  /** COMPONENT MOUNT */
  const [staffData, setStaffData] = useState<StaffDataType[]>([]);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [assignmentModal, setAssignmentModal] = useState<{
    show: boolean;
    staff: StaffDataType | null;
  }>({
    show: false,
    staff: null,
  });
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [paginationParams, setPaginationParams] = useState<IPaginationParams>({
    page: 1,
    totalPages: 1,
    totalItems: 1,
    from: 1,
    to: 1,
  });
  const [totalPages, setTotalPage] = useState(1);
  const [isArchive, setIsAchive] = useState(false);
  const [editForm, setEditForm] = useState({
    id: "",
    surname: "",
    firstname: "",
    othername: "",
    gender: "",
    date_of_birth: "",
    email: "",
    phone_number: "",
    classroom_id: "",
    house_address: "",
    house_address2: "",
    city: "",
    state: "",
    country: "",
    zip_code: "",
    profile_picture: "",
    signature: "",
    school_id: "",
    degree: "",
    discipline: "",
    class_type: "Secondary",
    institution: "DEFAULT",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
  });

  const [imageUploaded, setImageUploaded] = useState<ImageObject>({
    //Image user uploaded
    profile_picture: null,
    signature: null,
  });
  const [initialImage, setInitialImage] = useState<ImageObject>({
    //Image to display
    profile_picture: null,
    signature: null,
  });
  const [editMode, setEditMode] = useState(false);

  /** FILTERINGS */
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter] = useState("");
  const [filteredData, setFilteredData] = useState(staffData);
  const [changeClassMode] = useState(false);

  /** DATA FETCHING */
  async function fetchStaffData() {
    toast.remove();
    toast.loading("Fetching staff data...");
    setIsLoading(true);
    try {
      const response = await makeFetchRequest(
        `${defaultUrl}/teachers?page=${page}?surname=${searchTerm ? searchTerm : ""
        }`,
        true,
        false,
      );

      setPaginationParams({
        ...paginationParams,
        page: response?.data?.meta?.current_page,
        totalPages: response?.data?.meta?.last_page,
        from: response?.data?.meta?.from,
        to: response?.data?.meta?.to,
        totalItems: response?.data?.meta?.total,
      });

      const data = response.data?.data.map((staff: any) => {
        return {
          ...staff,
        };
      });
      // SORT staff BY FIRST NAME
      const sortByName =
        data &&
        data.sort(function (a: any, b: any) {
          return a?.firstname.localeCompare(b.firstname);
        });

      toast.remove();
      toast.success("Fetched Successfully");
      setStaffData(sortByName);
      setTotalPage(data?.data?.meta?.last_page);
      setIsLoading(false);
    } catch (err: any) {
      toast.remove();
      toast.error("Error Fetching Staff Data");
    }
  }

  const [classrooms, setClassrooms] = useState([]);
  const fetchClassrooms = async () => {
    const response = await makeFetchRequest(
      `${defaultUrl}/classroom`,
      false,
      true,
    );
    setClassrooms(response || []);
    return response;
  };

  function filterData() {
    const filtered = staffData
      .filter((data) => {
        if (!searchTerm.length) {
          return data;
        }
        const fullName = `${data.firstname ?? ""} ${data.othername ?? ""}  ${data.surname ?? ""
          } ${data.lastname ?? ""}`;
        return fullName.toLowerCase().includes(searchTerm.toLowerCase());
      })
      .filter((data) => {
        if (!roleFilter.length || !data.role) {
          // console.log(roleFilter.length, {role: data})
          return data;
        }
        return data.role?.toLowerCase() === roleFilter.toLowerCase();
      });

    // console.log({ filtered, staffData });
    setFilteredData(filtered);
    return filtered;
  }

  useEffect(() => {
    const searchBar = document.getElementById("searchStaffInput");
    searchBar?.focus();
    fetchStaffData();
    fetchClassrooms();
  }, [isArchive]);

  useEffect(() => {
    filterData();
  }, [searchTerm, roleFilter, staffData]);
  function handleClickEdit(data: any) {
    setEditForm(data);
    setInitialImage({ profile_picture: null, signature: null });
    setImageUploaded({ profile_picture: null, signature: null });
    setEditMode(true);
  }

  async function addStaffToClass({ classroom, teacher_id }: any) {
    const confirmAction = await Swal.fire({
      title: `Confirm Action`,
      text: `Are you sure you want to add Staff to this Class?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "navyblue",
      confirmButtonText: "Confirm",
    });
    if (confirmAction.isConfirmed) {
      const body = {
        name: classroom.name,
        teachers: [...classroom.teachers, teacher_id],
      };
      await makePutRequest(`${defaultUrl}/classroom/${classroom?.id}`, body);
      setIsAchive(!isArchive);
    }
  }

  const handleUpdateAssignments = async () => {
    if (!assignmentModal.staff) return;

    setIsSaving(true);
    try {
      // 1. Get the original IDs the staff was assigned to
      const originalIds =
        assignmentModal.staff.classrooms?.map((c: any) => c.id) || [];

      // 2. Filter for IDs that were in the original list but are NOT in the new selection
      // These are the IDs the user "unselected"
      const unassignedIds = originalIds.filter(
        (id) => !selectedClassIds.includes(id),
      );

      if (unassignedIds.length === 0) {
        toast.error("No classrooms were unselected.");
        setIsSaving(false);
        return;
      }

      const body = {
        classrooms: unassignedIds, // Sending ONLY the unselected IDs
      };

      await makePostRequest(
        `${defaultUrl}/unassign-teacher/${assignmentModal.staff.id}`,
        body,
      );

      toast.success("Staff successfully unassigned from selected classes");
      setAssignmentModal({ show: false, staff: null });
      setIsAchive(!isArchive);
    } catch (error) {
      toast.error("Failed to unassign staff");
    } finally {
      setIsSaving(false);
    }
  };
  // function handleClickEdit(data: any) {
  //   // console.log({ data });
  //   setEditForm(data);
  //   setEditMode(true);
  // }

  function handleInputChange({ target: { name, value, files } }: any) {
    if (files?.length > 0) {
      const validation = validateUploadFile(files[0], "image");
      if (!validation.valid) {
        toast.error(validation.message);
        return;
      }
    }
    setEditForm((prev) => ({
      ...prev,
      [name]: files?.length > 0 ? files[0] : value,
    }));
    return;
  }

  // IMAGE ONCHAGE
  const handleImageUpload = (e: any) => {
    const { name } = e.target;
    const file = e.target.files[0];
    if (file) {
      const validation = validateUploadFile(file, "image");
      if (!validation.valid) {
        toast.error(validation.message);
        e.target.value = "";
        return;
      }
      setInitialImage((prev: any) => {
        return {
          ...prev,
          [name]: file,
        };
      });

      setImageUploaded((prev: any) => {
        return {
          ...prev,
          [name]: file,
        };
      });
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setIsSaving(true);
    const formData = editForm;

    // if media is available
    let profile_pictureString;
    let signature;
    if (imageUploaded?.profile_picture) {
      profile_pictureString = await uploadImageToCloudinary(
        imageUploaded?.profile_picture,
      );
      formData.profile_picture = profile_pictureString || "";
    }

    if (imageUploaded?.signature) {
      signature = await uploadImageToCloudinary(imageUploaded?.signature);
      formData.signature = signature || "";
    }

    const body = {
      surname: editForm.surname?.trim(),
      firstname: editForm.firstname.trim(),
      othername: editForm.othername?.trim(),
      gender: editForm.gender?.trim(),
      date_of_birth: editForm.date_of_birth?.trim(),
      email: editForm.email?.trim(),
      phone_number: editForm.phone_number?.trim(),
      // classroom_id: editForm.classroom_id?.trim(),
      house_address: editForm.house_address?.trim(),
      house_address2: editForm.house_address2?.trim(),
      city: editForm.city?.trim(),
      state: editForm.state?.trim(),
      country: editForm.country?.trim(),
      zip_code: editForm.zip_code?.trim(),
      profile_picture: formData.profile_picture?.trim(),
      signature: formData.signature?.trim(),
      school_id: editForm.school_id?.trim(),
      degree: editForm.degree?.trim(),
      discipline: editForm.discipline?.trim(),
      class_type: editForm.class_type?.trim(),
      institution: editForm.institution?.trim(),
      emergency_contact_name: editForm.emergency_contact_name?.trim(),
      emergency_contact_phone: editForm.emergency_contact_phone?.trim(),
      emergency_contact_relationship:
        editForm.emergency_contact_relationship?.trim(),
    };

    await makePutRequest(`${defaultUrl}/teachers/${editForm.id}`, body);

    setEditForm({
      id: "",
      surname: "",
      firstname: "",
      othername: "",
      gender: "",
      date_of_birth: "",
      email: "",
      phone_number: "",
      classroom_id: "",
      house_address: "",
      house_address2: "",
      city: "",
      state: "",
      country: "",
      // admission_date: "",
      zip_code: "",
      profile_picture: "",
      signature: "",
      school_id: "",
      institution: "DEFAULT",
      degree: "",
      discipline: "",
      class_type: "",
      emergency_contact_name: "",
      emergency_contact_phone: "",
      emergency_contact_relationship: "",
    });
    setEditMode(false);
    fetchStaffData();
    setIsSaving(false);
  };

  // put archive endpoint here
  function handleArchieve(id: string) {
    Swal.fire({
      icon: "warning",
      title: "Archive",
      text: "Are you sure you want to archive this staff?",
      showCancelButton: true,
      confirmButtonColor: "green",
      confirmButtonText: "Yes, archive",
      cancelButtonColor: "grey",
      cancelButtonText: "No, Cancel",
    }).then((response) => {
      if (response.isConfirmed) {
        makePutRequest(`${defaultUrl}/archiveTeacher/${id}`);
        setIsAchive(!isArchive);
      }
    });
  }

  const handleImpersonate = async (staff: StaffDataType) => {
    toast.remove();
    toast.loading(`Starting impersonation session for ${staff.firstname}...`);
    try {
      const adminToken = sessionStorage.getItem("token")?.replace(/['"]+/g, "");
      const res = await axios.post(
        `${defaultUrl}/impersonation/start`,
        {
          teacher_id: staff.user_id,
          reason: "reviewing teacher dashboard without poppin up modal",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
            Accept: "application/json",
          },
        },
      );

      toast.remove();
      const responseData = res?.data;
      const token =
        responseData?.token ||
        responseData?.data?.token ||
        responseData?.data?.data?.token;

      const userData = responseData?.data || responseData;

      if (token) {
        toast.success(`Impersonating ${staff.firstname} ${staff.surname}`);

        localStorage.setItem("temp_impersonate_token", token);
        localStorage.setItem(
          "temp_impersonate_user",
          JSON.stringify(userData),
        );
        localStorage.setItem("temp_is_impersonated", "true");

        window.open("/staff", "_blank");
      } else {
        toast.error("Could not retrieve impersonation token");
      }
    } catch (err: any) {
      toast.remove();
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to start impersonation session",
      );
    }
  };

  // RENDERED COMPONENT
  return (
    <>
      <div className="w-full flex items-center mb-4">
        <BackButton />
        <TopAdminNav name="Jane Doe" title="Staff Data" />
      </div>

      <div className="flex">
        <div className="block w-full h-[87vh] overflow-auto register-scroll">
          <div className="flex px-1 mb-5">
            <div className="w-full">
              <RegisterCard
                title="Staff Management"
                details="View and Manage all School Staff"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 mt-4 max-w-[98%] mx-auto">
            {/* Filter Search Component Container */}
            <div className="flex flex-wrap justify-start items-center text-[#1f1f1f]/[0.7] mb-4 w-full">
              <div className="w-full max-w-sm relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <FontAwesomeIcon icon={faSearch} className="text-xs" />
                </div>
                <input
                  id="searchStaffInput"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search staff members..."
                  className="w-full rounded-xl pl-9 pr-4 py-2 text-xs font-medium text-slate-700 bg-slate-50/50 border border-slate-200 placeholder-slate-400 focus:outline-none focus:border-[#0096D1] focus:bg-white transition-all"
                  type="text"
                />
              </div>
            </div>

            {/* Premium Synchronized Table Interface */}
            <div className="w-full overflow-x-auto rounded-xl border border-slate-50">
              <table className="w-full border-collapse text-left table-fixed min-w-[850px]">
                <thead>
                  <tr className="bg-[#F4F7FE] border-b border-slate-100 text-[11px] font-bold tracking-wider text-[#012063] uppercase">
                    <th className="py-3.5 px-4 w-[12%] text-center">Photo</th>
                    <th className="py-3.5 px-4 w-[23%] text-left">Name</th>
                    <th className="py-3.5 px-4 w-[18%] text-left">Class</th>
                    <th className="py-3.5 px-4 w-[22%] text-left">
                      Contact Info
                    </th>
                    <th className="py-3.5 px-4 w-[15%] text-left">
                      Emergency Contact
                    </th>
                    <th className="py-3.5 px-4 w-[10%] text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {isLoading ? (
                    <tr>
                      <td colSpan={6} className="py-12">
                        <div className="flex justify-center items-center">
                          <img
                            src={loader}
                            alt="loader"
                            className="w-10 h-10 object-contain"
                          />
                        </div>
                      </td>
                    </tr>
                  ) : staffData.length > 0 ? (
                    filteredData.map((item: any, index: number) => {
                      const fullName = `${item.firstname ?? ""} ${item.othername ?? ""
                        }  ${item.surname ?? ""} ${item.lastname ?? ""}`;
                      return (
                        <tr
                          key={index}
                          className="group bg-white hover:bg-slate-50/40 transition-colors duration-150 text-[13px] text-slate-600 font-medium"
                        >
                          {/* 1. Photo Cell */}
                          <td className="py-3 px-4 text-center whitespace-nowrap">
                            <div className="inline-flex h-12 w-12 items-center justify-center overflow-hidden rounded-full bg-slate-50 border border-slate-100 shadow-sm">
                              <LazyLoadImage
                                style={{
                                  height: "100%",
                                  width: "100%",
                                  objectFit: "cover",
                                }}
                                src={item?.profile_picture}
                                effect="blur"
                                wrapperProps={{
                                  style: {
                                    transitionDelay: "1s",
                                    height: "100%",
                                    width: "100%",
                                  },
                                }}
                                alt=""
                              />
                            </div>
                          </td>

                          {/* 2. Full Name Cell */}
                          <td className="py-3 px-4">
                            <div className="font-bold text-slate-800 tracking-tight capitalize truncate group-hover:text-[#0096D1] transition-colors">
                              {fullName}
                            </div>
                            <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-400 font-semibold tracking-wider uppercase mt-1 inline-block">
                              ID: #{item.id?.substring(0, 5) || "N/A"}
                            </span>
                          </td>

                          {/* 3. Class Designation */}
                          <td className="py-3 px-4">
                            <div className="truncate text-slate-700 font-semibold">
                              {item?.classrooms &&
                                item.classrooms.length > 0 ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs">
                                  {item.classrooms[0]?.name}
                                </span>
                              ) : (
                                <span className="text-slate-400 text-xs font-normal italic">
                                  Unassigned
                                </span>
                              )}
                            </div>
                          </td>

                          {/* 4. Primary Contact Info */}
                          <td className="py-3 px-4">
                            <div className="text-slate-700 font-semibold text-xs tracking-tight">
                              {item.phone_number ?? "—"}
                            </div>
                            <div
                              className="text-slate-400 text-[11px] truncate mt-0.5"
                              title={item.email}
                            >
                              {item.email ?? "—"}
                            </div>
                          </td>

                          {/* 5. Emergency Contacts */}
                          <td className="py-3 px-4">
                            <div className="text-slate-700 font-semibold text-xs tracking-tight">
                              {item.emergency_contact_phone ?? "—"}
                            </div>
                            <div className="text-slate-400 text-[11px] truncate mt-0.5 capitalize">
                              {item.emergency_contact_relationship
                                ? `${item.emergency_contact_relationship} • `
                                : ""}
                              {item.emergency_contact_name ?? ""}
                            </div>
                          </td>

                          {/* 6. Context Action Menus */}
                          <td className="py-4 px-4 text-center whitespace-nowrap align-middle">
                            <Menu
                              menuButton={
                                <MenuButton className="inline-flex items-center justify-center gap-1.5 rounded-xl px-4 py-2 bg-slate-50 border border-slate-200 text-slate-700 text-xs font-bold shadow-sm hover:bg-slate-100 hover:border-slate-300 active:scale-95 transition-all duration-150">
                                  Options
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    strokeWidth={2.5}
                                    stroke="currentColor"
                                    className="w-3 h-3 text-slate-500 group-hover:text-slate-700"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                                    />
                                  </svg>
                                </MenuButton>
                              }
                              transition
                              align="end"
                              offsetY={6}
                              menuClassName="bg-white border border-slate-100 rounded-xl shadow-xl p-1.5 min-w-[170px]"
                            >
                              {/* Edit Profile Action */}
                              <MenuItem
                                onClick={() => handleClickEdit(item)}
                                className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-600 rounded-lg cursor-pointer hover:bg-sky-50 hover:text-[#0096D1] transition-colors duration-150"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                  className="w-4 h-4 opacity-75"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
                                  />
                                </svg>
                                Edit Profile
                              </MenuItem>

                              {/* Unassign Classroom Action */}
                              <MenuItem
                                onClick={() => {
                                  setAssignmentModal({
                                    show: true,
                                    staff: item,
                                  });
                                  setSelectedClassIds(
                                    item.classrooms?.map((c: any) => c.id) ||
                                    [],
                                  );
                                }}
                                className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-slate-600 rounded-lg cursor-pointer hover:bg-orange-50 hover:text-orange-600 transition-colors duration-150"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                  className="w-4 h-4 opacity-75"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                                Unassign Class
                              </MenuItem>

                              {/* Impersonate Staff Action */}
                              <MenuItem
                                onClick={() => handleImpersonate(item)}
                                className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-purple-600 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors duration-150"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                  className="w-4 h-4 opacity-75"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
                                  />
                                </svg>
                                Impersonate Staff
                              </MenuItem>

                              {/* Divider Separation */}
                              <div className="my-1 border-t border-slate-50" />

                              {/* Archive Danger Action */}
                              <MenuItem
                                onClick={() => handleArchieve(item.id)}
                                className="flex items-center gap-2.5 px-3 py-2 text-xs font-semibold text-rose-600 rounded-lg cursor-pointer hover:bg-rose-50 transition-colors duration-150"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={2}
                                  stroke="currentColor"
                                  className="w-4 h-4 opacity-75"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5m8.25 3v6.75m0 0l-3-3m3 3l3-3M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                                  />
                                </svg>
                                Archive Profile
                              </MenuItem>
                            </Menu>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={6}
                        className="py-8 text-center text-slate-400 text-sm"
                      >
                        No active staff records cataloged...
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls Footer Component Row */}
            <div className="mt-4 pt-2 border-t border-slate-50">
              <Pagination
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                paginationParams={paginationParams}
              />
            </div>
          </div>
        </div>
      </div>

      {editMode && (
        <div className="">
          <CustomModal
            title={`Edit Staff - ${editForm.surname}`}
            width={75}
            children={
              <EditStaffForm
                initialImage={initialImage}
                form={editForm}
                handleChange={(e: any) => handleInputChange(e)}
                handleImageUpload={handleImageUpload}
                handleSubmit={handleSubmit}
                isLoading={isSaving}
              />
            }
            hideModal={() => setEditMode((prev) => !prev)}
          />
        </div>
      )}
      {changeClassMode && (
        <div className="">
          <CustomModal
            title={`Edit Staff - ${editForm.surname}`}
            width={75}
            children={
              <EditStaffForm
                form={editForm}
                handleChange={(e: any) => handleInputChange(e)}
                handleSubmit={handleSubmit}
              />
            }
            hideModal={() => setEditMode((prev) => !prev)}
          />
        </div>
      )}
      {assignmentModal.show && (
        <CustomModal
          title="Manage Class Assignments"
          width={60}
          hideModal={() => setAssignmentModal({ show: false, staff: null })}
        >
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-xl font-bold text-[#012063]">
                {assignmentModal.staff?.firstname}{" "}
                {assignmentModal.staff?.surname}
              </h3>
              <p className="text-sm text-gray-500">
                Uncheck the classes you want to{" "}
                <span className="text-red-500 font-bold">unassign</span> this
                staff from.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[45vh] overflow-y-auto p-2 register-scroll">
              {assignmentModal.staff?.classrooms?.map((clas: any) => {
                const isSelected = selectedClassIds.includes(clas.id);

                return (
                  <div
                    key={clas.id}
                    onClick={() => {
                      setSelectedClassIds((prev) =>
                        isSelected
                          ? prev.filter((id) => id !== clas.id)
                          : [...prev, clas.id],
                      );
                    }}
                    className={`relative overflow-hidden transition-all duration-300 cursor-pointer p-4 rounded-2xl border-2 flex flex-col justify-between h-28 ${isSelected
                      ? "bg-white border-[#0096D1] shadow-md shadow-blue-100"
                      : "bg-red-50 border-red-200 shadow-inner opacity-80"
                      }`}
                  >
                    <div className="flex justify-between items-start">
                      <span
                        className={`font-bold text-sm ${isSelected ? "text-[#012063]" : "text-red-700"}`}
                      >
                        {clas.name}
                      </span>
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${isSelected
                          ? "bg-[#0096D1] border-[#0096D1]"
                          : "bg-white border-red-400"
                          }`}
                      >
                        {isSelected && (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            className="text-white text-xs"
                          />
                        )}
                      </div>
                    </div>

                    <p
                      className={`text-[11px] uppercase tracking-wider font-semibold ${isSelected ? "text-blue-400" : "text-red-400"}`}
                    >
                      {isSelected ? "Assigned" : "Marked for Removal"}
                    </p>
                  </div>
                );
              })}
            </div>

            <div className="mt-10 flex items-center justify-between border-t pt-6">
              <div className="text-sm text-gray-400 italic">
                {assignmentModal.staff?.classrooms?.length! -
                  selectedClassIds.length}{" "}
                classroom(s) will be removed.
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() =>
                    setAssignmentModal({ show: false, staff: null })
                  }
                  className="px-6 py-2.5 rounded-xl text-gray-500 font-semibold hover:bg-gray-100 transition-all"
                >
                  Cancel
                </button>
                <button
                  disabled={isSaving}
                  onClick={handleUpdateAssignments}
                  className={`px-8 py-2.5 rounded-xl font-bold text-white transition-all transform active:scale-95 shadow-lg ${isSaving
                    ? "bg-gray-400"
                    : "bg-[#012063] hover:bg-[#0096D1] shadow-blue-200"
                    }`}
                >
                  {isSaving ? "Processing..." : "Confirm Changes"}
                </button>
              </div>
            </div>
          </div>
        </CustomModal>
      )}
    </>
  );
};
